import { API_ORIGIN } from "@/lib/apiOrigin";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useGoogleMaps } from "@/lib/maps";
import { api } from "@/lib/api";
import { MapPin, Loader2, Search } from "lucide-react";

const SEARCH_TIMEOUT_MS = 15000;

/** Google Places when available; otherwise an explicit server search.
 * The server may use Nominatim, so typing and prefilled values never trigger
 * server lookups. Search and selection results belong to their original query.
 */
export default function AddressAutocompleteInput({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "",
  className = "input-tall w-full",
  dataTestid,
  required,
  publicMode = false,
  onKeyDown: externalKeyDown,
  onFocus: externalFocus,
  onBlur: externalBlur,
  ...rest
}) {
  const { ready, error, google, config } = useGoogleMaps();
  const [failedSdk, setFailedSdk] = useState(null);
  const canUseGoogle = Boolean(ready && !error && google?.maps?.places?.Autocomplete && google !== failedSdk);
  const [usingGoogle, setUsingGoogle] = useState(false);
  const googleActive = canUseGoogle && usingGoogle;
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const focusedRef = useRef(false);
  const mountedRef = useRef(true);
  const valueRef = useRef(value || "");
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  valueRef.current = value || "";
  onChangeRef.current = onChange;
  onPlaceSelectedRef.current = onPlaceSelected;

  const [suggestions, setSuggestions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [provider, setProvider] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState("");
  const resultsRef = useRef(null);
  const selectingRef = useRef(false);
  const operationRef = useRef(0);
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);
  const uid = useId();
  const listId = `address-results-${uid}`;
  const hintId = `address-hint-${uid}`;
  const statusId = `address-status-${uid}`;
  const testId = suffix => dataTestid ? `${dataTestid}-${suffix}` : `address-${suffix}`;

  const cancelPending = useCallback(() => {
    operationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    selectingRef.current = false;
  }, []);
  const clearResults = useCallback(() => {
    resultsRef.current = null;
    setSuggestions([]);
    setOpenDropdown(false);
    setProvider(null);
    setHighlight(0);
    setBusy(null);
    setMessage("");
  }, []);
  const active = id => mountedRef.current && operationRef.current === id;
  const beginOperation = phase => {
    cancelPending();
    const id = operationRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(phase);
    timeoutRef.current = setTimeout(() => {
      if (!active(id)) return;
      cancelPending();
      setBusy(null);
      setMessage("La ricerca ha impiegato troppo tempo. Premi Cerca per riprovare.");
    }, SEARCH_TIMEOUT_MS);
    return { id, signal: controller.signal };
  };
  const finishOperation = id => {
    if (!active(id)) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    abortRef.current = null;
    selectingRef.current = false;
    setBusy(null);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; cancelPending(); };
  }, [cancelPending]);

  // An externally supplied prefill, audience change or SDK transition also
  // invalidates any old results. This effect performs no network lookup.
  useEffect(() => {
    cancelPending();
    clearResults();
  }, [value, publicMode, canUseGoogle, cancelPending, clearResults]);

  useEffect(() => {
    if (focusedRef.current) inputRef.current?.focus();
  }, [canUseGoogle]);

  useEffect(() => {
    if (!canUseGoogle || !inputRef.current) {
      setUsingGoogle(false);
      return;
    }
    let alive = true;
    let autocomplete;
    let listener;
    try {
      const opts = { fields: ["formatted_address", "geometry", "place_id", "name", "address_components"], language: "it" };
      const sa = config?.service_area;
      if (sa?.bounds) {
        opts.bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(sa.bounds.sw.lat, sa.bounds.sw.lng),
          new google.maps.LatLng(sa.bounds.ne.lat, sa.bounds.ne.lng),
        );
        opts.strictBounds = config?.service_area_mode === "ticino";
      }
      if (sa?.country) opts.componentRestrictions = { country: sa.country.split("|").map(c => c.toLowerCase()) };
      autocomplete = new google.maps.places.Autocomplete(inputRef.current, opts);
      listener = autocomplete.addListener("place_changed", () => {
        if (!alive) return;
        const place = autocomplete.getPlace();
        if (!place?.geometry) return;
        const address = place.formatted_address || inputRef.current?.value || "";
        onChangeRef.current?.({ target: { value: address } });
        onPlaceSelectedRef.current?.({ address,
          lat: place.geometry.location.lat(), lng: place.geometry.location.lng(),
          place_id: place.place_id, name: place.name, source: "google_js",
          struct: extractStructured(place.address_components),
        });
      });
      setUsingGoogle(true);
    } catch {
      setUsingGoogle(false);
      setFailedSdk(google);
    }
    return () => {
      alive = false;
      if (listener?.remove) listener.remove();
      else if (autocomplete) google?.maps?.event?.clearInstanceListeners?.(autocomplete);
    };
  }, [canUseGoogle, google, config]);

  useEffect(() => {
    const closeOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        cancelPending();
        setBusy(null);
        setOpenDropdown(false);
        setMessage("");
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [cancelPending]);

  const search = async () => {
    if (googleActive || rest.disabled || rest.readOnly) return;
    const query = valueRef.current.trim();
    clearResults();
    cancelPending();
    if (query.length < 2) {
      setMessage("Inserisci almeno 2 caratteri dell’indirizzo, poi premi Cerca.");
      inputRef.current?.focus();
      return;
    }
    const { id, signal } = beginOperation("search");
    setMessage("Ricerca in corso…");
    try {
      let data;
      if (publicMode) {
        const response = await fetch(`${API_ORIGIN}/api/public/places?q=${encodeURIComponent(query)}&limit=6`, { signal });
        if (!response.ok) throw new Error("search_unavailable");
        data = await response.json();
      } else {
        const response = await api.get("/maps/places", { params: { q: query, limit: 6 }, signal });
        data = response.data;
      }
      if (!active(id) || valueRef.current.trim() !== query) return;
      if (!Array.isArray(data?.results)) throw new Error("invalid_search_response");
      const items = data.results.filter(item => typeof item?.address === "string" && item.address.trim());
      resultsRef.current = { query, items, publicMode };
      setSuggestions(items);
      setProvider(data?.provider || null);
      setHighlight(0);
      setOpenDropdown(items.length > 0);
      setMessage(items.length ? `${items.length} risultati. Seleziona l’indirizzo corretto.` : "Nessun risultato. Aggiungi via, numero civico e località, poi premi Cerca.");
    } catch {
      if (active(id)) setMessage("Ricerca non disponibile. Premi Cerca per riprovare oppure completa manualmente l’indirizzo dove previsto.");
    } finally {
      finishOperation(id);
    }
  };

  const pick = async suggestion => {
    const result = resultsRef.current;
    if (googleActive || selectingRef.current || !result || result.publicMode !== publicMode
        || result.query !== valueRef.current.trim() || !result.items.includes(suggestion)) return;
    const { id, signal } = beginOperation("pick");
    selectingRef.current = true;
    setOpenDropdown(false);
    setMessage("Completo l’indirizzo selezionato…");
    let struct;
    try {
      // Reverse lookup is only for an explicit authenticated selection.
      if (!publicMode && Number.isFinite(suggestion.lat) && Number.isFinite(suggestion.lng)) {
        const response = await api.post("/maps/reverse-geocode", { lat: suggestion.lat, lng: suggestion.lng }, { signal });
        if (response.data?.found) struct = {
          street: response.data.street, house_number: response.data.house_number,
          zip: response.data.zip, city: response.data.city,
          canton: (response.data.canton || "").toUpperCase().slice(0, 4),
          country: response.data.country || "Svizzera",
        };
      }
    } catch { /* The selected text remains useful if structured details are unavailable. */ }
    if (!active(id) || valueRef.current.trim() !== result.query) return;
    finishOperation(id);
    resultsRef.current = null;
    setSuggestions([]);
    setMessage("");
    onChangeRef.current?.({ target: { value: suggestion.address } });
    onPlaceSelectedRef.current?.({ address: suggestion.address, lat: suggestion.lat, lng: suggestion.lng,
      place_id: suggestion.place_id, name: suggestion.name, source: suggestion.source, struct });
    inputRef.current?.focus();
  };

  const handleChange = event => {
    valueRef.current = event.target.value;
    cancelPending();
    clearResults();
    onChange?.(event);
  };
  const handleKeyDown = event => {
    externalKeyDown?.(event);
    if (event.defaultPrevented || googleActive || event.isComposing || event.nativeEvent?.isComposing) return;
    if (event.key === "Escape") {
      event.preventDefault();
      cancelPending();
      setBusy(null);
      setOpenDropdown(false);
      setMessage("");
    } else if ((event.key === "ArrowDown" || event.key === "ArrowUp") && suggestions.length) {
      event.preventDefault();
      setOpenDropdown(true);
      setHighlight(index => event.key === "ArrowDown" ? Math.min(suggestions.length - 1, index + 1) : Math.max(0, index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (busy) return;
      if (openDropdown && suggestions.length) pick(suggestions[highlight]);
      else search();
    }
  };
  const listOpen = !googleActive && openDropdown && suggestions.length > 0;

  return (
    <div className="relative min-w-0" ref={wrapperRef}>
      <div className={googleActive ? "min-w-0" : "grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2"}>
        <input
          {...rest}
          key={canUseGoogle ? "google-input" : "server-input"}
          ref={inputRef}
          value={value || ""}
          onChange={handleChange}
          onFocus={event => { focusedRef.current = true; externalFocus?.(event); }}
          onBlur={event => { focusedRef.current = false; externalBlur?.(event); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`${className} min-w-0 max-w-full`}
          data-testid={dataTestid}
          required={required}
          autoComplete="off"
          enterKeyHint={googleActive ? undefined : "search"}
          aria-label={rest["aria-label"] || (rest["aria-labelledby"] ? undefined : (placeholder || "Cerca indirizzo"))}
          role={googleActive ? undefined : "combobox"}
          aria-autocomplete={googleActive ? undefined : "list"}
          aria-expanded={googleActive ? undefined : Boolean(listOpen)}
          aria-controls={listOpen ? listId : undefined}
          aria-activedescendant={listOpen ? `${listId}-${highlight}` : undefined}
          aria-describedby={[rest["aria-describedby"], !googleActive && hintId, !googleActive && statusId].filter(Boolean).join(" ") || undefined}
        />
        {!googleActive && <button
          type="button"
          data-testid={testId("search-button")}
          disabled={Boolean(busy || rest.disabled || rest.readOnly)}
          onClick={search}
          className="min-h-[48px] rounded-xl border border-dapnow-line bg-white px-3 py-3 text-sm font-semibold text-dapnow-ink hover:bg-dapnow-bg disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : <Search aria-hidden="true" className="size-4" />}
          Cerca
        </button>}
      </div>
      {googleActive ? <span data-testid={testId("maps-active")} className="mt-1 inline-flex rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">Maps</span> : <>
        <p id={hintId} className="mt-1 text-xs text-dapnow-muted">Digita l’indirizzo e premi Cerca. Poi seleziona un risultato.</p>
        <p id={statusId} role="status" aria-live="polite" className="mt-1 text-xs text-dapnow-muted" data-testid={testId("search-status")}>{message}</p>
      </>}
      {listOpen && <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-2xl border border-dapnow-line bg-white shadow-xl py-1">
        <ul id={listId} role="listbox" aria-label="Risultati della ricerca indirizzo" data-testid={testId("suggestions")}>
          {suggestions.map((suggestion, index) => {
            const showName = Boolean(suggestion.name) && suggestion.name !== suggestion.address.split(",")[0];
            return <li
              key={`${suggestion.place_id || index}-${index}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlight}
              data-testid={testId(`suggestion-${index}`)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => pick(suggestion)}
              onMouseEnter={() => setHighlight(index)}
              className={`px-3 py-3 cursor-pointer flex items-start gap-2.5 ${index === highlight ? "bg-dapnow-blue-soft" : "hover:bg-dapnow-bg/60"}`}
            >
              <MapPin aria-hidden="true" className="h-4 w-4 text-dapnow-blue mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                {showName && <p className="text-sm font-semibold tracking-tight break-words">{suggestion.name}</p>}
                <p className={`text-xs break-words ${showName ? "text-dapnow-muted" : "text-dapnow-ink"}`}>{suggestion.address}</p>
              </div>
            </li>;
          })}
        </ul>
        {["google", "nominatim"].includes(provider) && <p className="px-3 py-1.5 text-[10px] text-dapnow-muted border-t border-dapnow-line">
          Risultati via {provider === "nominatim" ? <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline">© OpenStreetMap</a> : "Google Maps"}
        </p>}
      </div>}
    </div>
  );
}

/**
 * Phase 32 Batch A — extract structured fields from Google Places address_components.
 * Returns: { street, house_number, zip, city, canton, country }
 */
function extractStructured(components) {
  if (!Array.isArray(components)) return null;
  const out = { street: "", house_number: "", zip: "", city: "", canton: "", country: "Svizzera" };
  for (const c of components) {
    const types = c.types || [];
    if (types.includes("route")) out.street = c.long_name;
    else if (types.includes("street_number")) out.house_number = c.long_name;
    else if (types.includes("postal_code")) out.zip = c.long_name;
    else if (types.includes("locality") || types.includes("postal_town")) out.city = c.long_name;
    else if (types.includes("administrative_area_level_1") || types.includes("administrative_area_level_2")) {
      if (!out.canton) out.canton = c.short_name || c.long_name;
    } else if (types.includes("country")) out.country = c.long_name || out.country;
  }
  if (!out.city) {
    for (const c of components) {
      if ((c.types || []).includes("sublocality") || (c.types || []).includes("postal_town")) {
        out.city = c.long_name;
        break;
      }
    }
  }
  return out;
}
